import { ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { StorefrontProductSummary } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

export interface ProductCardPreviewProps {
  product: StorefrontProductSummary;
}

/** A resolved rail product: image, title, price in the previewed market, compare-at struck through. */
export function ProductCardPreview({ product }: ProductCardPreviewProps) {
  const { t } = useTranslation('content');
  const format = useFormat();
  const image = product.featuredMedia;
  const src = image ? (image.renditions?.medium ?? image.url) : null;
  const price = product.price;
  const ranged = price !== null && price.min.amount !== price.max.amount;
  const compareAt =
    product.compareAtMin && price && product.compareAtMin.amount > price.min.amount
      ? product.compareAtMin
      : null;

  return (
    <li className="flex min-w-0 flex-col gap-1.5" aria-label={product.title}>
      <div className="bg-muted aspect-square overflow-hidden rounded-md">
        {src ? (
          <img src={src} alt={image?.alt ?? ''} className="size-full object-cover" loading="lazy" />
        ) : (
          <div className="text-muted-foreground flex size-full items-center justify-center">
            <ImageOff className="size-5" aria-hidden />
          </div>
        )}
      </div>
      <p className="line-clamp-2 text-xs font-medium">{product.title}</p>
      <p className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
        {price ? (
          <span>
            {ranged
              ? t('blockPreview.product.from', { price: format.money(price.min) })
              : format.money(price.min)}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('blockPreview.product.noPrice')}</span>
        )}
        {compareAt ? <s className="text-muted-foreground">{format.money(compareAt)}</s> : null}
      </p>
    </li>
  );
}
